import React, { useRef, useState } from "react";
import "filepond/dist/filepond.min.css";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import { toast } from "react-toastify";
import {
  isAlreadyDeletedError,
  mediaApi,
  useDeleteMediaMutation,
  useGetMediaQuery,
} from "@/redux/reducer/media.api";
import { useDispatch } from "react-redux";
import {
  BootstrapSizes,
  Box,
  Button,
  ButtonColors,
  Modal,
  Pagination,
  Select,
  Typography,
} from "@talentech/components";
import { IMediaFile } from "@/service/service";
import { useSelector } from "react-redux";
import { getUser } from "@talentech/bff-login";

// Register FilePond plugins
registerPlugin(FilePondPluginImagePreview, FilePondPluginFileValidateType);

interface CustomAssetManagerProps {
  onSelect: (src: string) => void;
  onSelectDubleClick?: (src: string) => void;
}

const mediaUploadUrl = `${process.env.API_URL}/media/`;

const CustomAssetManager: React.FC<CustomAssetManagerProps> = ({
  onSelect,
  onSelectDubleClick,
}) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<{ value: number; label: string }>({
    label: "24",
    value: 24,
  });
  const { data, isFetching, isError } = useGetMediaQuery(
    { page, page_size: pageSize.value },
    {
      skip: page === 0,
    }
  );

  const { tenantId } = useSelector(getUser);

  const [size, setSize] = useState("big");
  const [showModal, setShowModal] = useState(false);
  const [asset, setAsset] = useState<IMediaFile | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const pondRef = useRef<FilePond>(null);
  const handleBrowseClick = () => {
    if (pondRef.current) {
      pondRef.current?.browse();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // Prevent default to allow drop
    setIsDraggingOver(true); // Show message when dragging over
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false); // Hide message when leaving drop area
  };

  const onCancelDelete = () => {
    setShowModal(false);
    setAsset(null);
  };

  const onSelectDelete = (file: IMediaFile) => {
    setAsset(file);
    setShowModal(true);
  };

  const [deleteMedia] = useDeleteMediaMutation();
  const onConfirmDelete = () => {
    if (!asset) {
      return;
    }
    const deletion = deleteMedia(asset.name)
      .unwrap()
      .catch((error) => {
        // 404 = the tile was stale and the file is already gone — success
        // from the user's point of view; the gallery refetch tidies the grid
        if (!isAlreadyDeletedError(error)) {
          throw error;
        }
      });
    toast
      .promise(deletion, {
        error: "Failed to delete image",
        pending: "Deleting image...",
        success: "Image deleted successfully",
      })
      .then(() => {
        setShowModal(false);
      });
  };

  const onCopyToClipboard = (asset: IMediaFile) => {
    navigator.clipboard
      .writeText(asset.link)
      .then(() => {
        toast.success("Image URL copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy URL to clipboard");
      });
  };

  if (isError) {
    return <p>Error loading media files.</p>;
  }

  return (
    <div>
      <Box flex className="align-items-center">
        <Box>
          {[
            {
              size: "small",
              icon: "minimize",
            },
            {
              size: "big",
              icon: "maximize",
            },
          ].map(({ size: s, icon }) => (
            <Button
              onClick={() => setSize(s)}
              icon={<i className={`fa-sharp fa-light fa-arrows-${icon}`} />}
              className="me-1"
              outline
              key={s}
              disabled={size === s}
              color={ButtonColors.PRIMARY}
            />
          ))}
        </Box>

        <Box className="flex-grow-1 ms-2">
          <Button
            label="Upload from your computer"
            size={BootstrapSizes.SMALL}
            icon={<i className="fa-sharp fa-light fa-upload me-2" />}
            onClick={handleBrowseClick}
            outline
            color={ButtonColors.SECONDARY}
          />
        </Box>

        <Box flex className="align-items-center">
          <Typography className="m-0 me-1">Page size:</Typography>
          <Select
            options={[
              { label: "24", value: 24 },
              { label: "48", value: 48 },
            ]}
            value={pageSize}
            onChange={(option: any) => {
              setPageSize(option);
            }}
          />
        </Box>
      </Box>

      <div
        className="d-flex flex-wrap mt-4 row position-relative custom-filepond"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {(data?.files || []).map((asset, index) => (
          <figure
            key={asset.name + index}
            // ref={index === files.length - 1 ? lastFileRef : null}
            className={`col-${
              size === "small" ? "1" : "2"
            } media-library-item__wrapper`}
          >
            <div className="mt-3 rounded">
              {asset.extension === "mp4" ? (
                <div className="media-library-item__video">
                  <video
                    src={asset.link}
                    className="cursor-pointer img-fluid  rounded border border-light-subtle media-library-item"
                    onClick={() => onSelect(asset.link)}
                  onDoubleClick={() => onSelectDubleClick?.(asset.link)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onSelect(asset.link);
                    }
                  }}
                  />
                </div>
              ) : (
                <img
                  tabIndex={0}
                  src={`${asset.thumbnail}`}
                  alt={asset.name}
                  title={asset.name}
                  className="cursor-pointer img-fluid  rounded border border-light-subtle media-library-item"
                  style={{ objectFit: "cover" }}
                  onClick={() => onSelect(asset.link)}
                  onDoubleClick={() => onSelectDubleClick?.(asset.link)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onSelect(asset.link);
                    }
                  }}
                />
              )}
            </div>
            <div className="media-library-item__actions bg-white border border-light-subtle p-1 rounded">
              <i
                className="fa-sharp fa-light fa-trash me-2 btn btn-danger"
                onClick={() => onSelectDelete(asset)}
              />
              <i
                className="fa-sharp fa-light fa-copy btn btn-info"
                onClick={() => onCopyToClipboard(asset)}
              />
            </div>
            <span>
              <figcaption
                className="text-muted small d-block mt-1 text-truncate"
                title={asset.name.replaceAll(tenantId || "", "")}
              >
                {asset.name.replaceAll(tenantId || "", "")}
              </figcaption>
              <small className="text-muted small d-block mt-1">
                {asset.extension === "mp4"
                  ? "Video"
                  : `Size: ${asset.width} x ${asset.height}`}
              </small>
            </span>
          </figure>
        ))}

        <div
          style={{
            position: "absolute",
            height: "100%",
            width: "100%",
            display:
              isDraggingOver || (data?.files || []).length === 0
                ? "block"
                : "none",
            zIndex: isDraggingOver ? 10 : 1,
            pointerEvents: isDraggingOver
              ? "auto"
              : (data?.files || []).length === 0
              ? "auto"
              : "none",
          }}
        >
          <FilePond
            ref={pondRef}
            allowMultiple={false}
            acceptedFileTypes={["image/*", "video/mp4"]}
            server={{
              process: {
                url: mediaUploadUrl,
                withCredentials: true,
                method: "POST",
              },
            }}
            className={"file-pond-custom"}
            name="file"
            onprocessfiles={async () => {
              toast.success("File uploaded successfully");
              dispatch(mediaApi.util.invalidateTags(["Media"]));
              setIsDraggingOver(false);
            }}
          />
        </div>
      </div>
      {isFetching && <p>Loading...</p>}

      {data && (
        <Box flex className="justify-content-center mt-4 pt-4">
          <Pagination
            // @ts-ignore
            pageCount={data?.total_pages}
            onPageChange={(page: { selected: number }) => {
              setPage(page.selected + 1);
            }}
          />
        </Box>
      )}

      <Modal
        title="Delete image"
        showModal={showModal}
        onClose={onCancelDelete}
        buttons={[
          <Button
            onClick={onCancelDelete}
            label="Cancel"
            outline
            key={"cancel"}
          />,
          <Button
            onClick={onConfirmDelete}
            label="Delete"
            key={"delete"}
            color={ButtonColors.PRIMARY}
            icon={<i className="fa-sharp fa-trash me-2" />}
          />,
        ]}
      >
        {asset && (
          <>
            <img src={`${asset.link}?width=100`} alt={asset.name} />
            <p>
              Are you sure you want to delete <strong>{asset.name}</strong>?
            </p>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CustomAssetManager;
